import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Typography,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  Stack,
  InputAdornment
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  SummarizeOutlined as ChatSummaryIcon,
  TrendingUp as IndustryIcon,
  PersonOutline as ProfileIcon,
  EditNote as PulseIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ============================================================
// REPORT TYPE CONFIGURATION
// ============================================================
const REPORT_TYPE_CONFIG = {
  chat_summary: {
    label: 'Chat Summary',
    icon: ChatSummaryIcon,
    backgroundColor: '#fff8e1',     // amber-50
    borderColor: '#ffb300',          // amber-600
    chipColor: 'warning',
    titleColor: '#e65100',           // deep orange-900
    emptyMessage: 'No Chat Summary reports yet. Use the date pickers above to generate your first summary.'
  },
  industry_insights: {
    label: 'Industry Insights',
    icon: IndustryIcon,
    backgroundColor: '#e0f2f1',     // teal-50
    borderColor: '#00897b',          // teal-600
    chipColor: 'success',
    titleColor: '#004d40',           // teal-900
    emptyMessage: 'No Industry Insights reports yet. Select an industry above to generate cross-client analysis.'
  },
  client_profile: {
    label: 'Client Profile',
    icon: ProfileIcon,
    backgroundColor: '#e3f2fd',     // blue-50
    borderColor: '#1e88e5',          // blue-600
    chipColor: 'info',
    titleColor: '#0d47a1',           // blue-900
    emptyMessage: 'No Client Profile reports yet. Click "Generate Client Profile" above to create one.'
  },
  strategic_pulse: {
    label: 'Strategic Pulse',
    icon: PulseIcon,
    backgroundColor: '#e8eaf6',     // indigo-50
    borderColor: '#3949ab',          // indigo-600
    chipColor: 'default',
    titleColor: '#1a237e',           // indigo-900
  }
};

// Smart collapse threshold for Strategic Pulse (chars)
const SMART_COLLAPSE_THRESHOLD = 200;

// ============================================================
// HELPER: Format Firestore timestamp
// ============================================================
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// ============================================================
// HELPER: Generate preview snippet from content
// ============================================================
const getPreviewSnippet = (content, maxLength = 180) => {
  if (!content) return '';
  // Strip markdown formatting for the preview
  const plainText = content
    .replace(/#{1,6}\s/g, '')       // headers
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/\*([^*]+)\*/g, '$1')   // italic
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/[-*+]\s/g, '')         // list markers
    .replace(/\n+/g, ' ')           // newlines to spaces
    .trim();
  if (plainText.length <= maxLength) return plainText;
  return plainText.substring(0, maxLength).trim() + '…';
};

// ============================================================
// COMPONENT: ReportHistoryFeed
// ============================================================
const ReportHistoryFeed = ({ reportType, clientId, fcfoId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const config = REPORT_TYPE_CONFIG[reportType];
  const TypeIcon = config?.icon || ChatSummaryIcon;

  // ============================================================
  // FIRESTORE LISTENER
  // ============================================================
  useEffect(() => {
    // Determine if this report type should fall back to fcfoId when no specific client is selected
    const isCrossClientType = reportType === 'industry_insights';
    const usesFcfoFallback = reportType === 'client_profile' || reportType === 'chat_summary';
    const noClientSelected = !clientId || clientId === 'ALL';

    // Guard: need fcfoId for cross-client types; for per-client types with no client selected,
    // fall back to fcfoId query if supported, otherwise show empty
    if (isCrossClientType || (noClientSelected && usesFcfoFallback)) {
      if (!fcfoId) {
        setReports([]);
        setLoading(false);
        return;
      }
    } else {
      if (noClientSelected) {
        setReports([]);
        setLoading(false);
        return;
      }
    }

    setLoading(true);

    // Build query based on report type and client selection
    let q;
    if (isCrossClientType || (noClientSelected && usesFcfoFallback)) {
      // Query by fcfoId — either cross-client type, or per-client type with All Clients selected
      q = query(
        collection(db, 'clientReports'),
        where('fcfoId', '==', fcfoId),
        where('reportType', '==', reportType),
        orderBy('generatedAt', 'desc')
      );
    } else {
      // Per-client report types with a specific client selected
      q = query(
        collection(db, 'clientReports'),
        where('clientId', '==', clientId),
        where('reportType', '==', reportType),
        orderBy('generatedAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const reportDocs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setReports(reportDocs);
        setLoading(false);
      },
      (error) => {
        console.error(`[ReportHistoryFeed] Error fetching ${reportType} reports:`, error);
        setReports([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [reportType, clientId, fcfoId]);

  // ============================================================
  // EXPAND/COLLAPSE
  // ============================================================
  const toggleExpansion = (reportId) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(reportId)) {
        next.delete(reportId);
      } else {
        next.add(reportId);
      }
      return next;
    });
  };

  // ============================================================
  // SEARCH FILTER
  // ============================================================
  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return reports;
    const lowerQuery = searchQuery.toLowerCase();
    return reports.filter(report => {
      const titleMatch = (report.title || '').toLowerCase().includes(lowerQuery);
      const contentMatch = (report.content || '').toLowerCase().includes(lowerQuery);
      const industryMatch = (report.industry || '').toLowerCase().includes(lowerQuery);
      return titleMatch || contentMatch || industryMatch;
    });
  }, [reports, searchQuery]);

  // ============================================================
  // RENDER: Individual Report Card
  // ============================================================
  const renderReportCard = (report) => {
    const isExpanded = expandedItems.has(report.id);
    const content = report.content || '';

    // Strategic Pulse: smart collapse — show full text if short
    const isStrategicPulse = reportType === 'strategic_pulse';
    const isShortContent = isStrategicPulse && content.length <= SMART_COLLAPSE_THRESHOLD;

    return (
      <Card
        key={report.id}
        elevation={2}
        sx={{
          mb: 1.5,
          border: '1px solid',
          borderColor: config.borderColor,
          backgroundColor: config.backgroundColor,
          '&:hover': {
            boxShadow: 3
          }
        }}
      >
        <CardContent sx={{ pb: 1, '&:last-child': { pb: 1.5 } }}>
          {/* Header Row */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
            <Box flex={1} minWidth={0}>
              {/* Type Chip + Date Range Chip */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap">
                <Chip
                  icon={<TypeIcon sx={{ fontSize: 16 }} />}
                  label={config.label}
                  size="small"
                  color={config.chipColor}
                  variant="filled"
                  sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                />
                {report.dateFrom && report.dateTo && (
                  <Chip
                    icon={<DateRangeIcon sx={{ fontSize: 14 }} />}
                    label={`${report.dateFrom} — ${report.dateTo}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
                {report.industry && (
                  <Chip
                    label={report.industry}
                    size="small"
                    variant="outlined"
                    color={config.chipColor}
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Stack>

              {/* Title — clickable to expand */}
              <Typography
                variant="subtitle1"
                component="h3"
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: isShortContent ? 'default' : 'pointer',
                  mb: 0.25,
                  color: config.titleColor,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => !isShortContent && toggleExpansion(report.id)}
              >
                {report.title || config.label}
              </Typography>

              {/* Timestamp */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <CalendarIcon sx={{ fontSize: 13 }} />
                {formatTimestamp(report.generatedAt)}
              </Typography>
            </Box>

            {/* Expand/Collapse Button (hidden for short Strategic Pulse) */}
            {!isShortContent && (
              <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                <IconButton
                  onClick={() => toggleExpansion(report.id)}
                  size="small"
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  sx={{ ml: 1, mt: 0.5 }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Preview snippet (shown when collapsed, hidden when expanded) */}
          {!isExpanded && !isShortContent && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.75,
                fontSize: '0.85rem',
                lineHeight: 1.5,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {getPreviewSnippet(content)}
            </Typography>
          )}

          {/* Short Strategic Pulse: show full text inline (no expand needed) */}
          {isShortContent && (
            <Typography
              variant="body2"
              sx={{ mt: 0.75, fontSize: '0.9rem', lineHeight: 1.6 }}
            >
              {content}
            </Typography>
          )}

          {/* Expanded content */}
          {!isShortContent && (
            <Collapse in={isExpanded} timeout="auto">
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  '& h1': { fontSize: '1.3em', fontWeight: 700, mt: 2, mb: 1 },
                  '& h2': { fontSize: '1.15em', fontWeight: 600, mt: 1.5, mb: 0.75 },
                  '& h3': { fontSize: '1.05em', fontWeight: 600, mt: 1.25, mb: 0.5 },
                  '& p': { fontSize: '0.9rem', lineHeight: 1.7, mb: 1 },
                  '& ul, & ol': { pl: 3, mb: 1 },
                  '& li': { fontSize: '0.9rem', lineHeight: 1.6, mb: 0.25 },
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    mb: 2,
                    '& th, & td': {
                      border: '1px solid #ddd',
                      padding: '6px 10px',
                      fontSize: '0.85rem',
                      textAlign: 'left'
                    },
                    '& th': { backgroundColor: '#f5f5f5', fontWeight: 600 }
                  },
                  '& blockquote': {
                    borderLeft: '3px solid',
                    borderColor: config.borderColor,
                    pl: 2,
                    ml: 0,
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  },
                  '& code': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '0.85em',
                    fontFamily: 'monospace'
                  },
                  '& pre': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    padding: '12px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    '& code': {
                      backgroundColor: 'transparent',
                      padding: 0
                    }
                  }
                }}
              >
                {reportType === 'industry_insights' && report.structuredData ? (
                  // Industry Insights: render structured data with sections
                  renderIndustryInsightsExpanded(report.structuredData)
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {content || 'No content available.'}
                  </ReactMarkdown>
                )}
              </Box>
            </Collapse>
          )}
        </CardContent>
      </Card>
    );
  };

  // ============================================================
  // RENDER: Industry Insights structured data (expanded view)
  // ============================================================
  const renderIndustryInsightsExpanded = (data) => {
    if (!data) return <Typography variant="body2">No structured data available.</Typography>;

    return (
      <Box>
        {/* Executive Summary */}
        {data.executiveSummary && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Executive Summary
            </Typography>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {data.executiveSummary}
            </ReactMarkdown>
          </Box>
        )}

        {/* Top Themes */}
        {data.topThemes && data.topThemes.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Top Themes
            </Typography>
            {data.topThemes.map((theme, idx) => (
              <Box key={idx} mb={1}>
                <Typography variant="body2" fontWeight={500}>
                  {theme.theme || theme.title || `Theme ${idx + 1}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  {theme.description || theme.summary || ''}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Common Challenges */}
        {data.commonChallenges && data.commonChallenges.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Common Challenges
            </Typography>
            {data.commonChallenges.map((item, idx) => (
              <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                • {typeof item === 'string' ? item : (item.challenge || item.description || JSON.stringify(item))}
              </Typography>
            ))}
          </Box>
        )}

        {/* Shared Opportunities */}
        {data.sharedOpportunities && data.sharedOpportunities.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Shared Opportunities
            </Typography>
            {data.sharedOpportunities.map((item, idx) => (
              <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                • {typeof item === 'string' ? item : (item.opportunity || item.description || JSON.stringify(item))}
              </Typography>
            ))}
          </Box>
        )}

        {/* Recommended Actions */}
        {data.recommendedActions && data.recommendedActions.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Recommended Actions
            </Typography>
            {data.recommendedActions.map((item, idx) => (
              <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                • {typeof item === 'string' ? item : (item.action || item.description || JSON.stringify(item))}
              </Typography>
            ))}
          </Box>
        )}

        {/* Industry Signals */}
        {data.industrySignals && data.industrySignals.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Industry Signals
            </Typography>
            {data.industrySignals.map((signal, idx) => (
              <Box key={idx} mb={1}>
                <Typography variant="body2" fontWeight={500}>
                  {signal.signal || signal.title || `Signal ${idx + 1}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  {signal.implication || signal.description || ''}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  if (!config) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
        Unknown report type: {reportType}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Section Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, fontSize: '0.95rem', color: 'text.primary' }}
        >
          Report History
          {reports.length > 0 && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              ({reports.length} report{reports.length !== 1 ? 's' : ''})
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Search Bar (only show when there are reports) */}
      {reports.length > 0 && (
        <TextField
          size="small"
          placeholder={`Search ${config.label.toLowerCase()} reports…`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
          sx={{
            mb: 1.5,
            width: '100%',
            maxWidth: 400,
            '& .MuiInputBase-root': { fontSize: '0.85rem' }
          }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" alignItems="center" gap={1} py={3} justifyContent="center">
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading report history…
          </Typography>
        </Box>
      )}

      {/* Empty State */}
      {!loading && reports.length === 0 && (
        <Box
          sx={{
            py: 3,
            px: 2,
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider'
          }}
        >
          <TypeIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {config.emptyMessage}
          </Typography>
        </Box>
      )}

      {/* No Search Results */}
      {!loading && reports.length > 0 && filteredReports.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No reports matching "{searchQuery}"
        </Typography>
      )}

      {/* Report Cards */}
      {!loading && filteredReports.map(report => renderReportCard(report))}
    </Box>
  );
};

export default ReportHistoryFeed;